import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Select, {components, MenuListProps} from 'react-select'
import {searchPlatformFieldDataAsync} from 'src/redux/actions/platformSettingActions'
import {getPlatformParentFieldDataCategoriesAsync} from 'src/redux/actions/templateAndPricingActions'
import {useAppDispatch} from 'src/redux/storeHooks'

type PlatformFieldDataOption = {
  value: number
  label: string
  raw?: any
}

type PlatformFieldDataSearchSelectProps = {
  field?: any
  inputId?: string
  instanceId?: string
  isMulti?: boolean
  isClearable?: boolean
  placeholder?: string
  selectedData?: any
  selectedIds?: any[]
  onChange: (...args: any[]) => void
  styles?: any
  options?: PlatformFieldDataOption[]
  value?: any
  [key: string]: any
}

const MAX_DEFAULT_OPTIONS = 100
const MIN_SEARCH_LENGTH = 1
const SEARCH_DEBOUNCE_MS = 400
const PARENT_PAGE_SIZE = 50

type PaginationMode = 'search' | 'parent' | 'local' | null

const isAbortedRequest = (error: any): boolean =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED'

const getResponseData = (payload: any): any[] => {
  if (Array.isArray(payload?.results)) {
    return payload.results
  }

  const data = payload?.data ?? payload

  if (Array.isArray(data)) {
    return data
  }

  if (Array.isArray(data?.data)) {
    return data.data
  }

  if (Array.isArray(data?.results)) {
    return data.results
  }

  return []
}

const getNextUrl = (payload: any): string | null => {
  const next = payload?.next ?? payload?.data?.next
  return typeof next === 'string' && next.length > 0 ? next : null
}

const hasPaginatedNextPage = (payload: any): boolean => Boolean(getNextUrl(payload) || payload?.next)

const toOption = (item: any): PlatformFieldDataOption | null => {
  if (!item || item.id === null || item.id === undefined) {
    return null
  }

  return {
    value: Number(item.id),
    label: `${item.api_name ?? item.display_name ?? item.name ?? item.api_id ?? item.id}`,
    raw: item,
  }
}

const mergeUniqueOptions = (options: PlatformFieldDataOption[]): PlatformFieldDataOption[] => {
  const seen = new Set<number>()
  return options.filter((option) => {
    if (seen.has(option.value)) {
      return false
    }
    seen.add(option.value)
    return true
  })
}

const filterOptionsBySearch = (
  optionList: PlatformFieldDataOption[],
  query: string
): PlatformFieldDataOption[] => {
  const normalized = query.trim().toLowerCase()
  if (!normalized) {
    return optionList
  }

  return optionList.filter((option) => option.label.toLowerCase().includes(normalized))
}

const ensurePlatformFieldDataSet = (field: any) => {
  if (!field) {
    return
  }

  if (!Array.isArray(field.platformfielddata_set)) {
    try {
      field.platformfielddata_set = []
    } catch {
      // Ignore if read-only / frozen object
    }
  }
}

export const buildPlatformSearchField = (platformField: any, fieldData: any[] = []) => {
  if (!platformField) {
    return undefined
  }

  return {
    ...platformField,
    platformfielddata_set:
      fieldData?.length > 0 ? fieldData : platformField.platformfielddata_set ?? [],
  }
}

/**
 * Resolve a SELECT option id from a saved fieldmappingdata row.
 * After default-mapping saves, the API often returns only
 * `destination_platform_data_str` (with `destination_platform_data: null`).
 */
export const resolveSavedDestinationPlatformDataId = (
  item:
    | {
        destination_platform_data?: {id?: string | number | null} | null
        destination_platform_data_id?: string | number | null
        destination_platform_data_str?: string | null
      }
    | null
    | undefined
): string => {
  if (!item) {
    return ''
  }

  const fromObject = item.destination_platform_data?.id
  if (fromObject !== null && fromObject !== undefined && `${fromObject}` !== '') {
    return String(fromObject)
  }

  const fromId = item.destination_platform_data_id
  if (fromId !== null && fromId !== undefined && `${fromId}` !== '') {
    return String(fromId)
  }

  const str = item.destination_platform_data_str
  if (str === null || str === undefined) {
    return ''
  }

  const trimmed = `${str}`.trim()
  if (!trimmed || trimmed === 'true' || trimmed === 'false') {
    return ''
  }

  const asNum = Number(trimmed)
  if (Number.isNaN(asNum) || !Number.isFinite(asNum)) {
    return ''
  }

  return trimmed
}

export const toPlatformFieldSelectValue = (
  selectedId: string | number | null | undefined,
  fieldData: any[] = []
) => {
  if (selectedId === null || selectedId === undefined || selectedId === '') {
    return null
  }

  const numericId = Number(selectedId)
  if (Number.isNaN(numericId)) {
    return null
  }

  const matched = fieldData?.find((item) => Number(item.id) === numericId)
  if (matched) {
    return {
      value: Number(matched.id),
      label: `${matched.api_name ?? matched.display_name ?? matched.id}`,
      raw: matched,
    }
  }

  // Keep the selection visible while option lists hydrate (e.g. after save when
  // destination_platform_data is null and only the id string is returned).
  return {
    value: numericId,
    label: String(selectedId),
  }
}

export const toPlatformFieldMultiSelectValue = (
  selectedIds: Array<string | number> | string | number | null | undefined,
  fieldData: any[] = []
) => {
  const ids = Array.isArray(selectedIds)
    ? selectedIds
    : selectedIds !== null && selectedIds !== undefined && selectedIds !== ''
    ? [selectedIds]
    : []

  return ids
    .map((selectedId) => toPlatformFieldSelectValue(selectedId, fieldData))
    .filter(Boolean)
}

export const appendPlatformFieldData = (fieldData: any[] = [], raw: any) => {
  if (!raw?.id) {
    return fieldData
  }

  const exists = fieldData.some((item) => Number(item.id) === Number(raw.id))
  return exists ? fieldData : [...fieldData, raw]
}

type PlatformFieldSearchSelectInternals = {
  menuListNodeRef: React.MutableRefObject<HTMLDivElement | null>
  savedScrollTopRef: React.MutableRefObject<number>
  pendingScrollRestoreRef: React.MutableRefObject<number | null>
  onMenuScroll: (event: React.UIEvent<HTMLDivElement>) => void
  isLoadingMore: boolean
}

const LOADING_MORE_ROW_STYLES = `
  @keyframes platformFieldSelectDotPulse {
    0%, 80%, 100% {
      opacity: 0.25;
      transform: scale(0.85);
    }
    40% {
      opacity: 1;
      transform: scale(1);
    }
  }
  .platform-field-select-loading-dots span {
    display: inline-block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: #6c757d;
    margin-left: 3px;
    animation: platformFieldSelectDotPulse 1.2s infinite ease-in-out both;
  }
  .platform-field-select-loading-dots span:nth-child(2) {
    animation-delay: 0.15s;
  }
  .platform-field-select-loading-dots span:nth-child(3) {
    animation-delay: 0.3s;
  }
`

const LoadingMoreOptionsRow = () => (
  <div
    className='d-flex align-items-center justify-content-between px-3 py-2'
    style={{cursor: 'default', pointerEvents: 'none', fontSize: '14px', color: '#6c757d'}}
    role='status'
    aria-live='polite'
  >
    <span>loading more options</span>
    <span className='platform-field-select-loading-dots' aria-hidden='true'>
      <span />
      <span />
      <span />
    </span>
  </div>
)

const PlatformFieldSearchInternalsContext = createContext<PlatformFieldSearchSelectInternals | null>(
  null
)

const PaginatedMenuList = (props: MenuListProps<PlatformFieldDataOption, boolean>) => {
  const internals = useContext(PlatformFieldSearchInternalsContext)
  const {ref: menuRef, ...menuInnerProps} = props.innerProps

  useLayoutEffect(() => {
    if (!internals || internals.pendingScrollRestoreRef.current === null) {
      return
    }

    const scrollTop = internals.pendingScrollRestoreRef.current
    const menuNode = internals.menuListNodeRef.current

    if (!menuNode) {
      return
    }

    menuNode.scrollTop = scrollTop
    internals.savedScrollTopRef.current = scrollTop

    if (Math.abs(menuNode.scrollTop - scrollTop) < 2) {
      internals.pendingScrollRestoreRef.current = null
      return
    }

    // Avoid fighting user scroll when a stale restore position cannot be applied.
    if (!internals.isLoadingMore) {
      internals.pendingScrollRestoreRef.current = null
    }
  }, [internals, props.children])

  return (
    <components.MenuList
      {...props}
      innerProps={{
        ...menuInnerProps,
        ref: (node: HTMLDivElement | null) => {
          if (internals) {
            internals.menuListNodeRef.current = node
          }

          if (typeof menuRef === 'function') {
            menuRef(node)
          } else if (menuRef && typeof menuRef === 'object') {
            ;(menuRef as React.MutableRefObject<HTMLDivElement | null>).current = node
          }
        },
        onScroll: (event) => {
          if (internals) {
            internals.savedScrollTopRef.current = event.currentTarget.scrollTop
          }

          menuInnerProps?.onScroll?.(event)

          if (internals) {
            internals.onMenuScroll(event as React.UIEvent<HTMLDivElement>)
          }
        },
      }}
    >
      {props.children}
      {internals?.isLoadingMore && <LoadingMoreOptionsRow />}
    </components.MenuList>
  )
}

const PlatformFieldDataSearchSelect = ({
  field,
  inputId,
  instanceId,
  isMulti = false,
  isClearable = true,
  placeholder = 'Search and select',
  selectedData,
  selectedIds = [],
  onChange,
  styles,
  options = [],
  value,
  ...rest
}: PlatformFieldDataSearchSelectProps) => {
  const dispatch = useAppDispatch()
  const [searchOptions, setSearchOptions] = useState<PlatformFieldDataOption[]>([])
  const [hasLoadedRemoteOptions, setHasLoadedRemoteOptions] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchAbortControllerRef = useRef<AbortController | null>(null)
  const paginationAbortControllerRef = useRef<AbortController | null>(null)
  const activeSearchRef = useRef('')
  const nextUrlRef = useRef<string | null>(null)
  const paginationModeRef = useRef<PaginationMode>(null)
  const parentPageRef = useRef<number | null>(null)
  const localOptionsOffsetRef = useRef(MAX_DEFAULT_OPTIONS)
  const isLoadingMoreRef = useRef(false)
  const initialFetchDoneRef = useRef(false)
  const menuListNodeRef = useRef<HTMLDivElement | null>(null)
  const savedScrollTopRef = useRef(0)
  const pendingScrollRestoreRef = useRef<number | null>(null)

  const resetRemoteOptionsState = useCallback(() => {
    setSearchOptions([])
    setHasLoadedRemoteOptions(false)
    initialFetchDoneRef.current = false
    nextUrlRef.current = null
    paginationModeRef.current = null
    parentPageRef.current = null
    localOptionsOffsetRef.current = MAX_DEFAULT_OPTIONS
    pendingScrollRestoreRef.current = null
    savedScrollTopRef.current = 0
  }, [])

  useEffect(() => {
    resetRemoteOptionsState()
  }, [field?.id, resetRemoteOptionsState])

  const cancelPendingSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }

    searchAbortControllerRef.current?.abort()
    searchAbortControllerRef.current = null
  }, [])

  useEffect(() => {
    return () => {
      cancelPendingSearch()
      paginationAbortControllerRef.current?.abort()
    }
  }, [cancelPendingSearch])

  const captureMenuScrollPosition = useCallback(() => {
    const scrollTop = menuListNodeRef.current?.scrollTop ?? savedScrollTopRef.current

    if (scrollTop > 0) {
      savedScrollTopRef.current = scrollTop
      pendingScrollRestoreRef.current = scrollTop
    }

    return scrollTop
  }, [])

  const defaultOptions = useMemo(() => {
    const fieldOptions =
      field?.platformfielddata_set
        ?.slice(0, MAX_DEFAULT_OPTIONS)
        ?.map(toOption)
        ?.filter(Boolean) ?? []
    const passedOptions = options.slice(0, MAX_DEFAULT_OPTIONS) ?? []

    const selectedItems = Array.isArray(selectedData)
      ? selectedData
      : selectedData
      ? [selectedData]
      : []

    const selectedOptions = selectedItems.map(toOption).filter(Boolean)

    return mergeUniqueOptions([
      ...(selectedOptions as PlatformFieldDataOption[]),
      ...fieldOptions,
      ...passedOptions,
    ])
  }, [field?.id, field?.platformfielddata_set, selectedData, options])

  const selectedValue = useMemo(() => {
    if (isMulti) {
      if (Array.isArray(value)) {
        return value
      }
      return defaultOptions.filter((option) => selectedIds.map(Number).includes(option.value))
    }

    if (value) {
      return value?.raw ? value : {...value, raw: value.raw ?? selectedData}
    }

    const selectedItemOption = toOption(selectedData)
    if (selectedItemOption) {
      return selectedItemOption
    }

    const selectedId = selectedIds.length ? Number(selectedIds[0]) : null
    return defaultOptions.find((option) => option.value === selectedId) ?? null
  }, [defaultOptions, isMulti, selectedData, selectedIds, value])

  const displayedOptions = useMemo(() => {
    const selected = selectedValue
      ? Array.isArray(selectedValue)
        ? selectedValue
        : [selectedValue]
      : []
    const trimmedInput = inputValue.trim()
    const isSearching = trimmedInput.length >= MIN_SEARCH_LENGTH

    if (isSearching) {
      if (hasLoadedRemoteOptions && searchOptions.length > 0) {
        return mergeUniqueOptions([...(selected as PlatformFieldDataOption[]), ...searchOptions])
      }

      return mergeUniqueOptions([
        ...(selected as PlatformFieldDataOption[]),
        ...filterOptionsBySearch(defaultOptions, trimmedInput),
      ])
    }

    if (hasLoadedRemoteOptions && searchOptions.length > 0) {
      return mergeUniqueOptions([...(selected as PlatformFieldDataOption[]), ...searchOptions])
    }

    return mergeUniqueOptions([...(selected as PlatformFieldDataOption[]), ...defaultOptions])
  }, [defaultOptions, hasLoadedRemoteOptions, inputValue, searchOptions, selectedValue])

  const updateNextUrl = useCallback((url: string | null) => {
    nextUrlRef.current = url
  }, [])

  const initLocalPaginationIfNeeded = useCallback(() => {
    const localSet = field?.platformfielddata_set ?? []
    if (localSet.length <= MAX_DEFAULT_OPTIONS) {
      return false
    }

    paginationModeRef.current = 'local'
    parentPageRef.current = null
    updateNextUrl(null)
    localOptionsOffsetRef.current = MAX_DEFAULT_OPTIONS

    const initialOptions = localSet
      .slice(0, MAX_DEFAULT_OPTIONS)
      .map(toOption)
      .filter(Boolean) as PlatformFieldDataOption[]

    setSearchOptions(initialOptions)
    setHasLoadedRemoteOptions(true)
    return true
  }, [field?.platformfielddata_set, updateNextUrl])

  const fetchParentFieldDataPage = useCallback(
    async ({
      platformFieldId,
      page,
      searchString,
      append,
    }: {
      platformFieldId: number | string
      page: number
      searchString: string
      append: boolean
    }) => {
      const response = await dispatch(
        getPlatformParentFieldDataCategoriesAsync({
          platform_Id: platformFieldId,
          page,
          limit: PARENT_PAGE_SIZE,
          search_string: searchString,
          use_pagination: true,
        })
      )

      const remoteOptions = getResponseData(response.payload)
        .map(toOption)
        .filter(Boolean) as PlatformFieldDataOption[]

      setSearchOptions((prev) =>
        append ? mergeUniqueOptions([...prev, ...remoteOptions]) : remoteOptions
      )

      if (remoteOptions.length > 0) {
        setHasLoadedRemoteOptions(true)
      }

      if (hasPaginatedNextPage(response.payload)) {
        paginationModeRef.current = 'parent'
        parentPageRef.current = page + 1
        updateNextUrl(null)
      } else {
        if (paginationModeRef.current === 'parent') {
          paginationModeRef.current = null
        }
        parentPageRef.current = null
      }

      return remoteOptions
    },
    [dispatch, updateNextUrl]
  )

  const loadMoreLocalOptions = useCallback(() => {
    const localSet = field?.platformfielddata_set ?? []
    const offset = localOptionsOffsetRef.current

    if (offset >= localSet.length) {
      paginationModeRef.current = null
      return
    }

    const moreOptions = localSet
      .slice(offset, offset + MAX_DEFAULT_OPTIONS)
      .map(toOption)
      .filter(Boolean) as PlatformFieldDataOption[]

    if (!moreOptions.length) {
      paginationModeRef.current = null
      return
    }

    setSearchOptions((prev) => mergeUniqueOptions([...prev, ...moreOptions]))
    localOptionsOffsetRef.current = offset + MAX_DEFAULT_OPTIONS

    if (localOptionsOffsetRef.current >= localSet.length) {
      paginationModeRef.current = null
    }
  }, [field?.platformfielddata_set])

  const applySearchResults = useCallback(
    (payload: any, append: boolean, searchString = '') => {
      const remoteOptions = getResponseData(payload).map(toOption).filter(Boolean) as PlatformFieldDataOption[]

      setSearchOptions((prev) =>
        append ? mergeUniqueOptions([...prev, ...remoteOptions]) : remoteOptions
      )

      if (remoteOptions.length > 0 || searchString.trim().length >= MIN_SEARCH_LENGTH) {
        setHasLoadedRemoteOptions(true)
      }

      updateNextUrl(getNextUrl(payload))

      if (getNextUrl(payload)) {
        paginationModeRef.current = 'search'
        parentPageRef.current = null
      } else if (paginationModeRef.current === 'search') {
        paginationModeRef.current = null
      }

      return remoteOptions
    },
    [updateNextUrl]
  )

  const fetchOptions = useCallback(
    async (searchString: string) => {
      const platformFieldId = field?.id
      const trimmedSearch = searchString.trim()

      if (!platformFieldId) {
        setSearchOptions([])
        updateNextUrl(null)
        return
      }

      const abortController = new AbortController()

      cancelPendingSearch()
      paginationAbortControllerRef.current?.abort()
      paginationAbortControllerRef.current = null
      isLoadingMoreRef.current = false
      setIsLoadingMore(false)
      paginationModeRef.current = null
      parentPageRef.current = null
      localOptionsOffsetRef.current = MAX_DEFAULT_OPTIONS
      searchAbortControllerRef.current = abortController
      activeSearchRef.current = trimmedSearch

      setIsLoading(true)

      try {
        const response = await dispatch(
          searchPlatformFieldDataAsync({
            platform_field_id: platformFieldId,
            search_string: trimmedSearch,
            signal: abortController.signal,
          })
        )

        if (abortController.signal.aborted || activeSearchRef.current !== trimmedSearch) {
          return
        }

        if (searchPlatformFieldDataAsync.rejected.match(response)) {
          if (isAbortedRequest(response.error) || isAbortedRequest(response.payload)) {
            return
          }
        }

        const remoteOptions = applySearchResults(response.payload, false, trimmedSearch) ?? []

        if (
          remoteOptions.length === 0 &&
          !abortController.signal.aborted &&
          activeSearchRef.current === trimmedSearch
        ) {
          const parentOptions = await fetchParentFieldDataPage({
            platformFieldId,
            page: 1,
            searchString: trimmedSearch,
            append: false,
          })

          if (
            parentOptions.length === 0 &&
            !trimmedSearch &&
            !abortController.signal.aborted &&
            activeSearchRef.current === trimmedSearch
          ) {
            initLocalPaginationIfNeeded()
          }
        }
      } finally {
        if (searchAbortControllerRef.current === abortController) {
          setIsLoading(false)

          if (!trimmedSearch) {
            setHasLoadedRemoteOptions(true)
          }
        }
      }
    },
    [applySearchResults, cancelPendingSearch, dispatch, fetchParentFieldDataPage, field?.id, initLocalPaginationIfNeeded, updateNextUrl]
  )

  const loadMoreOptions = useCallback(async () => {
    if (isLoadingMoreRef.current) {
      return
    }

    if (paginationModeRef.current === 'local') {
      isLoadingMoreRef.current = true
      setIsLoadingMore(true)
      captureMenuScrollPosition()

      try {
        loadMoreLocalOptions()
      } finally {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
      return
    }

    if (paginationModeRef.current === 'parent' && parentPageRef.current && field?.id) {
      const abortController = new AbortController()
      paginationAbortControllerRef.current?.abort()
      paginationAbortControllerRef.current = abortController
      isLoadingMoreRef.current = true
      setIsLoadingMore(true)
      captureMenuScrollPosition()

      try {
        await fetchParentFieldDataPage({
          platformFieldId: field.id,
          page: parentPageRef.current,
          searchString: activeSearchRef.current,
          append: true,
        })
      } finally {
        isLoadingMoreRef.current = false
        if (!abortController.signal.aborted) {
          setIsLoadingMore(false)
        }
      }
      return
    }

    const currentNextUrl = nextUrlRef.current

    if (!currentNextUrl) {
      return
    }

    const abortController = new AbortController()
    paginationAbortControllerRef.current?.abort()
    paginationAbortControllerRef.current = abortController
    isLoadingMoreRef.current = true
    setIsLoadingMore(true)
    captureMenuScrollPosition()

    try {
      const response = await dispatch(
        searchPlatformFieldDataAsync({
          next_url: currentNextUrl,
          signal: abortController.signal,
        })
      )

      if (abortController.signal.aborted) {
        return
      }

      if (searchPlatformFieldDataAsync.rejected.match(response)) {
        if (isAbortedRequest(response.error) || isAbortedRequest(response.payload)) {
          return
        }
      }

      applySearchResults(response.payload, true)
    } finally {
      isLoadingMoreRef.current = false
      if (!abortController.signal.aborted) {
        setIsLoadingMore(false)
      }
    }
  }, [
    applySearchResults,
    captureMenuScrollPosition,
    dispatch,
    fetchParentFieldDataPage,
    field?.id,
    loadMoreLocalOptions,
  ])

  const handleMenuScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget
      savedScrollTopRef.current = target.scrollTop
      const isNearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 24

      if (isNearBottom) {
        captureMenuScrollPosition()
        loadMoreOptions()
      }
    },
    [captureMenuScrollPosition, loadMoreOptions]
  )

  const platformFieldSearchInternals = useMemo<PlatformFieldSearchSelectInternals>(
    () => ({
      menuListNodeRef,
      savedScrollTopRef,
      pendingScrollRestoreRef,
      onMenuScroll: handleMenuScroll,
      isLoadingMore,
    }),
    [handleMenuScroll, isLoadingMore]
  )

  const handleInputChange = (nextInputValue: string, actionMeta: {action: string}) => {
    if (actionMeta.action !== 'input-change') {
      return
    }

    setInputValue(nextInputValue)
    activeSearchRef.current = nextInputValue.trim()
    cancelPendingSearch()
    paginationAbortControllerRef.current?.abort()
    paginationAbortControllerRef.current = null
    isLoadingMoreRef.current = false
    setIsLoadingMore(false)

    const trimmedValue = nextInputValue.trim()

    pendingScrollRestoreRef.current = null
    savedScrollTopRef.current = 0

    searchTimeoutRef.current = setTimeout(() => {
      fetchOptions(trimmedValue)
    }, SEARCH_DEBOUNCE_MS)
  }

  const handleMenuOpen = useCallback(() => {
    if (!field?.id || initialFetchDoneRef.current) {
      return
    }

    initialFetchDoneRef.current = true
    fetchOptions('')
  }, [fetchOptions, field?.id])

  const handleMenuClose = useCallback(() => {
    pendingScrollRestoreRef.current = null
    savedScrollTopRef.current = 0
  }, [])

  const mergedStyles = useMemo(() => {
    const userStyles = styles ?? {}

    return {
      ...userStyles,
      menu: (base: any, state: any) => {
        const resolved =
          typeof userStyles.menu === 'function' ? userStyles.menu(base, state) : base

        return {
          ...resolved,
          zIndex: 9999,
        }
      },
      menuList: (base: any, state: any) => {
        const resolved =
          typeof userStyles.menuList === 'function' ? userStyles.menuList(base, state) : base

        return {
          ...resolved,
          overflowAnchor: 'none',
          paddingBottom: 0,
        }
      },
    }
  }, [styles])

  const handleChange = (selectedOption: any) => {
    const selectedOptions = Array.isArray(selectedOption)
      ? selectedOption
      : selectedOption
      ? [selectedOption]
      : []

    if (field) {
      ensurePlatformFieldDataSet(field)

      selectedOptions.forEach((option: PlatformFieldDataOption) => {
        if (!option?.raw?.id) {
          return
        }

        const currentSet = Array.isArray(field.platformfielddata_set)
          ? field.platformfielddata_set
          : []

        const exists = currentSet.some(
          (item: any) => Number(item?.id) === Number(option.raw.id)
        )

        if (!exists) {
          try {
            field.platformfielddata_set = [...currentSet, option.raw]
          } catch {
            // Ignore if field is a read-only / frozen object
          }
        }
      })
    }

    if (!isMulti) {
      setInputValue('')
    }

    onChange(selectedOption)
  }

  return (
    <PlatformFieldSearchInternalsContext.Provider value={platformFieldSearchInternals}>
      <style>{LOADING_MORE_ROW_STYLES}</style>
      <Select
        {...rest}
        className='react-select'
        classNamePrefix='select'
        captureMenuScroll={false}
        components={{...(rest.components ?? {}), MenuList: PaginatedMenuList}}
        filterOption={null}
        inputId={inputId}
        instanceId={instanceId}
        inputValue={inputValue}
        isClearable={isClearable}
        isLoading={isLoading}
        maxMenuHeight={300}
        menuPlacement='auto'
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuShouldScrollIntoView={false}
        isMulti={isMulti}
        isSearchable
        options={displayedOptions}
        placeholder={placeholder}
        noOptionsMessage={() => (isLoading ? 'Loading...' : 'No options')}
        value={selectedValue as any}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onMenuClose={handleMenuClose}
        onMenuOpen={handleMenuOpen}
        onMenuScrollToBottom={loadMoreOptions}
        styles={mergedStyles}
      />
    </PlatformFieldSearchInternalsContext.Provider>
  )
}

export default PlatformFieldDataSearchSelect
